import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type measurementVisualizationTableRowProps = {
  uniqueId: string;
  entityId: number;
  id: string;
  usedParameters: { id: number; label: string; value: string }[];
  measurementDevice: string;
  measurementDeviceTechnicalName: string;
  tsoId: number;
  aggregationInterval: string;
  aggregationIntervalType: string;
  aggregationIntervalId: number;
  start: string;
  end: string;
  parameters: string[];
};

export type measurementVisualizationActiveTableRowProps = measurementVisualizationTableRowProps & {
  order: number;
};

export type measurementVisualizationParameterProps = {
  start: string;
  end: string;
  aggregationInterval: string;
  aggregationIntervalId: number;
  aggregationIntervalType: string;
  deviceIds: { [key: string]: string[] };
  usedParameters: string[];
};

type initialStateProps = {
  parameters: measurementVisualizationParameterProps | {};
  tableRows: measurementVisualizationTableRowProps[];
  activeTableRows: measurementVisualizationActiveTableRowProps[];
  editingRow: string | undefined;
  newRow: boolean;
  layout: 'singleColumn' | 'doubleColumn';
  unsavedChanges: measurementVisualizationTableRowProps | undefined;
  hasUnsavedChanges: boolean;
};

const initialState: initialStateProps = {
  parameters: {},
  tableRows: [],
  activeTableRows: [],
  editingRow: undefined,
  layout: 'singleColumn',
  newRow: false,
  hasUnsavedChanges: false,
  unsavedChanges: undefined,
};

const measurementVisualization = createSlice({
  name: 'measurementVisualizationVisualization',
  initialState,
  reducers: {
    setParameters: (state, action: PayloadAction<measurementVisualizationParameterProps>) => {
      state.parameters = action.payload;
    },
    resetParameters: (state) => {
      state.parameters = [];
    },
    upsertTableRow: (state, action: PayloadAction<measurementVisualizationTableRowProps>) => {
      const newRow = action.payload;
      const newRowIndex = state.tableRows.findIndex(
        (row: measurementVisualizationTableRowProps) => row.uniqueId === newRow.uniqueId
      );
      if (newRowIndex !== -1) {
        state.tableRows[newRowIndex] = { ...state.tableRows[newRowIndex], ...newRow };
      } else {
        state.tableRows.push(newRow);
      }
    },
    removeTableRow: (state, action: PayloadAction<string>) => {
      state.tableRows = state.tableRows.filter((tableRow) => tableRow.uniqueId !== action.payload);
    },
    clearTableRows: (state) => {
      state.tableRows = [];
    },
    insertActiveTableRow: (state, action: PayloadAction<measurementVisualizationActiveTableRowProps>) => {
      const newActiveRow = action.payload;
      const newActiveRowIndex = state.activeTableRows.findIndex(
        (activeRow) => activeRow.uniqueId === newActiveRow.uniqueId
      );
      if (newActiveRowIndex !== -1) {
        state.activeTableRows[newActiveRowIndex] = { ...state.activeTableRows[newActiveRowIndex], ...newActiveRow };
      } else {
        state.activeTableRows.push(newActiveRow);
      }
    },
    removeActiveTableRow: (state, action: PayloadAction<string>) => {
      state.activeTableRows = state.activeTableRows
        .filter((activeTableRow) => activeTableRow.uniqueId !== action.payload)
        .map((row, index) => ({ ...row, order: index + 1 }));
    },
    clearActiveTableRows: (state) => {
      state.activeTableRows = [];
    },
    setEditingRow: (state, action: PayloadAction<string | undefined>) => {
      state.editingRow = action.payload;
    },
    setNewRow: (state, action: PayloadAction<boolean>) => {
      state.newRow = action.payload;
    },
    setLayout: (state, action: PayloadAction<'singleColumn' | 'doubleColumn'>) => {
      state.layout = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setUnsavedChanges: (state, action: PayloadAction<measurementVisualizationTableRowProps | undefined>) => {
      state.unsavedChanges = action.payload;
    },
  },
});

export const {
  setParameters,
  resetParameters,
  upsertTableRow,
  removeTableRow,
  clearTableRows,
  insertActiveTableRow,
  removeActiveTableRow,
  clearActiveTableRows,
  setEditingRow,
  setNewRow,
  setLayout,
  setHasUnsavedChanges,
  setUnsavedChanges,
} = measurementVisualization.actions;

export default measurementVisualization.reducer;
